import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, FileText, Globe, MousePointerClick, ShieldCheck, ShieldOff, Undo2 } from 'lucide-react';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import { useOverviewStore } from '@/stores/overviewStore';
import { usePlansStore } from '@/stores/plansStore';
import { client } from '@/lib/api';

/**
 * Account activity — the history of what ZAC did to the Zoom account (the
 * plans that ran: fixes, chat changes, reverts; write-access changes) plus
 * drift detections. Rendered beside the report.
 */

interface ActivityEntry {
  id: string;
  eventType: string;
  label: string;
  path?: string;
  outcome?: string;
  revertable: boolean;
  details?: string;
  createdAt: string;
}

const PAGE_SIZE = 20;

function entryIcon(entry: ActivityEntry) {
  switch (entry.eventType) {
    case 'access.write_enabled':
      return <ShieldCheck className="h-4 w-4 text-emerald-400" />;
    case 'access.write_disabled':
      return <ShieldOff className="h-4 w-4 text-muted-foreground" />;
    case 'zoom.drift':
      return <AlertTriangle className="h-4 w-4 text-amber-400" />;
    case 'report.generated':
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    default:
      return entry.path === 'harness' ? (
        <MousePointerClick className="h-4 w-4 text-primary" />
      ) : (
        <Globe className="h-4 w-4 text-primary" />
      );
  }
}

/** The plan behind an entry, when it is one. */
function planIdOf(entry: ActivityEntry): string | null {
  try {
    const d = JSON.parse(entry.details ?? '{}') as { planId?: unknown };
    return typeof d.planId === 'string' ? d.planId : null;
  } catch {
    return null;
  }
}

function channelsOf(entry: ActivityEntry): string[] {
  try {
    const d = JSON.parse(entry.details ?? '{}') as { channels?: unknown };
    return Array.isArray(d.channels) ? d.channels.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    return [];
  }
}

const TRIGGER_LABELS: Record<string, string> = {
  initial: 'first generation',
  manual: 'manual refresh',
  auto: 'after a fix',
  drift: 'after drift detection',
};

function entryMeta(entry: ActivityEntry): string {
  const parts: string[] = [];
  if (entry.eventType === 'zoom.drift') {
    try {
      const d = JSON.parse(entry.details ?? '{}') as { changeCount?: number };
      if (d.changeCount) parts.push(`${d.changeCount} setting${d.changeCount === 1 ? '' : 's'} changed outside ZAC`);
    } catch { /* meta is best-effort */ }
  }
  if (entry.eventType === 'report.generated') {
    try {
      const d = JSON.parse(entry.details ?? '{}') as { trigger?: string };
      if (d.trigger) parts.push(TRIGGER_LABELS[d.trigger] ?? d.trigger);
    } catch { /* meta is best-effort */ }
  }
  const channels = channelsOf(entry);
  if (channels.length > 0) parts.push(channels.map((c) => (c === 'api' ? 'via Zoom API' : c === 'browser' ? 'via your browser' : 'by you')).join(', '));
  else if (entry.path) parts.push(entry.path === 'api' ? 'via Zoom API' : 'via browser');
  if (entry.outcome && entry.outcome !== 'detected') parts.push(entry.outcome);
  return parts.join(' · ');
}

export function AccountActivity({ refreshKey, accountId }: { refreshKey?: number; accountId?: string }) {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState<Record<string, 'pending' | 'failed'>>({});
  const [revertNotes, setRevertNotes] = useState<Record<string, string>>({});

  const load = useCallback(async (offset: number, append: boolean) => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/zoom/report/activity?limit=${PAGE_SIZE}&offset=${offset}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) return;
      const body = (await res.json()) as { entries: ActivityEntry[]; total: number };
      setEntries((prev) => (append ? [...prev, ...body.entries] : body.entries));
      setTotal(body.total);
    } catch {
      // Non-critical section — fail quiet, the report above is the payload.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(0, false);
  }, [load, refreshKey]);

  // A revert is a plan of the undo actions (docs/plans/change-planner.md D10), approved and run in one
  // go — the click is the consent — and followed to its end; its line shows under the entry.
  const revert = useCallback(async (entry: ActivityEntry) => {
    const planId = planIdOf(entry);
    if (planId === null) return;
    setReverting((p) => ({ ...p, [entry.id]: 'pending' }));
    try {
      const { plan } = await client.plans.revertPlan(planId);
      let line = '';
      await usePlansStore.getState().approveAndRunFromChat(plan.id, false, (said) => { line = said; });
      const done = usePlansStore.getState().plansById[plan.id]?.status === 'completed';
      setRevertNotes((p) => ({ ...p, [entry.id]: line }));
      if (done) {
        setReverting((p) => { const n = { ...p }; delete n[entry.id]; return n; });
        await load(0, false); // the revert added its own entry
        if (accountId) void useOverviewStore.getState().silentRefresh(accountId);
      } else {
        setReverting((p) => ({ ...p, [entry.id]: 'failed' }));
      }
    } catch (err) {
      setRevertNotes((p) => ({ ...p, [entry.id]: err instanceof Error ? err.message : String(err) }));
      setReverting((p) => ({ ...p, [entry.id]: 'failed' }));
    }
  }, [load, accountId]);

  return (
    <div className="card overflow-hidden animate-fade-in">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
        <Activity className="h-4 w-4 text-primary" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Account Activity</h3>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">{total}</span>
        )}
      </div>
      {entries.length === 0 ? (
        <p className="px-4 py-5 text-xs text-muted-foreground">
          No actions yet — fixes ZAC applies to your account will show up here.
        </p>
      ) : (
        <ul className="max-h-[60vh] divide-y divide-border/60 overflow-y-auto">
          {entries.map((entry) => {
            const revState = reverting[entry.id];
            return (
              <li key={entry.id} className="flex items-start gap-2.5 px-4 py-3">
                <span className="mt-0.5 shrink-0">{entryIcon(entry)}</span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm leading-snug ${entry.outcome === 'failed' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {planIdOf(entry) ? <Link to={`/changes/${planIdOf(entry)}`} className="hover:underline">{entry.label}</Link> : entry.label}
                  </p>
                  {entryMeta(entry) && (
                    <p className="text-xs text-muted-foreground">{entryMeta(entry)}</p>
                  )}
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground/80">
                      {new Date(entry.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {entry.revertable && (
                      <button
                        onClick={() => void revert(entry)}
                        disabled={revState === 'pending'}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-60"
                      >
                        <Undo2 className="h-3 w-3" />
                        {revState === 'pending' ? 'Reverting…' : revState === 'failed' ? 'Retry revert' : 'Revert'}
                      </button>
                    )}
                  </div>
                  {revertNotes[entry.id] && <p className="mt-0.5 text-[11px] text-muted-foreground">{revertNotes[entry.id]}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {entries.length < total && (
        <div className="border-t border-border px-4 py-2.5">
          <button
            onClick={() => void load(entries.length, true)}
            disabled={loading}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
          >
            {loading ? 'Loading…' : `Show more (${total - entries.length} older)`}
          </button>
        </div>
      )}
    </div>
  );
}
